import { useState, useEffect } from "react";
import { Button, Typography, Box, CircularProgress } from "@mui/material";
import { Save, Refresh } from "@mui/icons-material";
import { toast } from "react-toastify";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { Setting, SettingsFormData } from "../../Utils/Types";
import { Vocabulary } from "../../Utils/Vocabulary";
import SettingField from "./SettingField";
import styles from "../../Styles/settings.module.css";
import { endpoints } from "../../Utils/UrlEnum";

export default function GeneralSettings() {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [formData, setFormData] = useState<SettingsFormData>({});
  const [originalData, setOriginalData] = useState<SettingsFormData>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Fetch settings from API on component mount
   */
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response = await getData(endpoints.settings.main);
        const settingsData: Setting[] = response.data;

        setSettings(settingsData);

        // Convert to form data format
        const initialFormData: SettingsFormData = {};
        settingsData.forEach((setting) => {
          initialFormData[setting.name] = setting.value;
        });

        setFormData(initialFormData);
        setOriginalData({ ...initialFormData });
      } catch (error) {
        console.error("Error fetching settings:", error);
        // No manual toast - getData already handles error notifications
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  /**
   * Handle field value changes
   */
  const handleFieldChange = (
    name: string,
    value: string | number | boolean | object
  ) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Save all settings to the backend
   */
  const handleSave = async () => {
    try {
      // Convert form data back to settings format for API
      const updatedSettings = settings.map((setting) => ({
        ...setting,
        value: formData[setting.name],
      }));

      await postData(endpoints.settings.saveAll, updatedSettings);

      setOriginalData({ ...formData });
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  /**
   * Reset form to original values
   */
  const handleReset = () => {
    setFormData({ ...originalData });
    toast.info(Vocabulary.resetSuccess);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box className={styles.generalSettingsHeader}>
        <Typography variant="body2" className={styles.generalSettingsSubtitle}>
          {Vocabulary.baseInfo}
        </Typography>
      </Box>

      {/* Settings Fields */}
      <Box className={styles.generalSettingsContent}>
        {settings.map((setting) => (
          <SettingField
            key={setting.name}
            setting={setting}
            value={formData[setting.name]}
            onChange={handleFieldChange}
          />
        ))}

        {/* Action Buttons */}
        <Box className={styles.actionsContainer}>
          <Button
            variant="outlined"
            className={`${styles.resetButton} ${styles.modernButton}`}
            startIcon={<Refresh />}
            onClick={handleReset}
          >
            {Vocabulary.reset}
          </Button>
          <Button
            variant="contained"
            className={`${styles.saveButton} ${styles.modernButton}`}
            startIcon={<Save />}
            onClick={handleSave}
          >
            {Vocabulary.save}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
